import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

const AuthContainer = styled.div`
  min-height: calc(100vh - 200px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: var(--background-dark);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 20% 50%, rgba(212, 175, 55, 0.05) 0%, transparent 50%),
                      radial-gradient(circle at 80% 80%, rgba(245, 241, 232, 0.2) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const AuthCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid var(--accent-color);
  border-radius: 24px;
  padding: 50px 40px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(212, 175, 55, 0.1);
  backdrop-filter: blur(20px);
  position: relative;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--primary-color), var(--accent-color), var(--primary-color));
    border-radius: 24px 24px 0 0;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: 32px;
  margin-bottom: 10px;
  text-align: center;
  font-weight: 700;
  
  span {
    color: var(--primary-color);
  }
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  text-align: center;
  margin-bottom: 30px;
  font-size: 15px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 30px;
  background: var(--background-accent);
  border-radius: 14px;
  padding: 6px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.06);
`;

const Tab = styled.button`
  flex: 1;
  padding: 13px;
  background: ${props => (props.$active ? 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))' : '#FFFFFF')};
  border: none;
  color: ${props => (props.$active ? '#FFFFFF' : 'var(--text-secondary)')};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(212, 175, 55, 0.2);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }
  
  &:hover::before {
    width: 300px;
    height: 300px;
  }

  &:hover {
    background: ${props => (props.$active ? 'linear-gradient(135deg, var(--secondary-color), var(--primary-color))' : 'var(--background-accent)')};
    color: ${props => (props.$active ? '#FFFFFF' : 'var(--primary-color)')};
    transform: translateY(-2px);
    box-shadow: ${props => (props.$active ? '0 4px 12px rgba(212, 175, 55, 0.3)' : 'none')};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
`;

const Input = styled.input`
  padding: 15px 20px;
  background: var(--background-lighter);
  border: 2px solid var(--border-color);
  border-radius: 12px;
  color: var(--text-primary);
  font-size: 15px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background: #FFFFFF;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const Button = styled.button`
  padding: 16px;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  border: none;
  border-radius: 10px;
  color: #FFFFFF;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
  box-shadow: 0 4px 15px rgba(212, 175, 55, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(212, 175, 55, 0.4);
  }

  &:disabled {
    background: var(--text-tertiary);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(244, 67, 54, 0.1);
  border: 2px solid #f44336;
  color: #d32f2f;
  padding: 14px;
  border-radius: 10px;
  font-size: 14px;
  margin-bottom: 16px;
  font-weight: 500;
`;

const SuccessMessage = styled.div`
  background: rgba(76, 175, 80, 0.1);
  border: 2px solid #4caf50;
  color: #2e7d32;
  padding: 14px;
  border-radius: 10px;
  font-size: 14px;
  margin-bottom: 16px;
  font-weight: 500;
`;

const AdminLink = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 20px;
  padding: 12px;
  background: var(--background-accent);
  border: 2px solid var(--primary-color);
  border-radius: 10px;
  color: var(--primary-color);
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--primary-color);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3);
  }
`;

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { signInWithEmail, signUpWithEmail } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (isLogin) {
        await signInWithEmail(email, password);
        setSuccess('Login successful! Redirecting...');
        setTimeout(() => navigate('/profile'), 1500);
      } else {
        if (!name || !phone || !address) {
          setError('Please fill in all fields');
          setLoading(false);
          return;
        }
        await signUpWithEmail(email, password, { name, phone, address });
        setSuccess('Account created successfully! Redirecting...');
        setTimeout(() => navigate('/profile'), 1500);
      }
    } catch (err) {
      // Handle Firebase auth errors
      let errorMessage = err.message || 'An error occurred';
      
      if (errorMessage.includes('EMAIL_EXISTS') || errorMessage.includes('email-already-in-use')) {
        errorMessage = 'This email is already registered. Please login instead.';
      } else if (errorMessage.includes('WEAK_PASSWORD') || errorMessage.includes('weak-password')) {
        errorMessage = 'Password should be at least 6 characters.';
      } else if (errorMessage.includes('INVALID_EMAIL') || errorMessage.includes('invalid-email')) {
        errorMessage = 'Please enter a valid email address.';
      } else if (errorMessage.includes('wrong-password')) {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (errorMessage.includes('user-not-found')) {
        errorMessage = 'No account found with this email.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContainer>
      <AuthCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Title>{isLogin ? 'Welcome Back' : 'Create Account'} <span>✨</span></Title>
        <Subtitle>
          {isLogin ? 'Sign in to your Istri Wala account' : 'Join Istri Wala Pressing Studio today'}
        </Subtitle>

        <TabContainer>
          <Tab
            $active={isLogin}
            onClick={() => {
              setIsLogin(true);
              setError('');
              setSuccess('');
            }}
            type="button"
          >
            Login
          </Tab>
          <Tab
            $active={!isLogin}
            onClick={() => {
              setIsLogin(false);
              setError('');
              setSuccess('');
            }}
            type="button"
          >
            Sign Up
          </Tab>
        </TabContainer>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <Form onSubmit={handleSubmit}>
          {!isLogin && (
            <>
              <FormGroup>
                <Label>Full Name</Label>
                <Input
                  type="text"
                  placeholder="Enter your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                />
              </FormGroup>

              <FormGroup>
                <Label>Phone Number</Label>
                <Input
                  type="tel"
                  placeholder="Enter your phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={loading}
                />
              </FormGroup>

              <FormGroup>
                <Label>Address</Label>
                <Input
                  type="text"
                  placeholder="Enter your address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  disabled={loading}
                />
              </FormGroup>
            </>
          )}

          <FormGroup>
            <Label>Email</Label>
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </FormGroup>

          <FormGroup>
            <Label>Password</Label>
            <Input
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
          </FormGroup>

          <Button type="submit" disabled={loading}>
            {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Create Account'}
          </Button>
        </Form>
      </AuthCard>
    </AuthContainer>
  );
};

export default Auth;
